import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, CarIcon, SmartphoneIcon, CheckCircleIcon, ClockIcon, UploadCloudIcon, AlertCircleIcon } from 'lucide-react';
import { formatNaira, formatDate } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface ClaimsProps {
  navigate: (screen: string) => void;
}
export function Claims({
  navigate
}: ClaimsProps) {
  const [showNewClaim, setShowNewClaim] = useState(false);
  const claims = [{
    id: 'CLM-8492',
    product: 'Motor Vehicle',
    icon: CarIcon,
    date: '2026-03-12',
    amount: 500000,
    status: 'review',
    statusText: 'Under Review',
    colorTheme: 'amber'
  }, {
    id: 'CLM-7102',
    product: 'Device Insurance',
    icon: SmartphoneIcon,
    date: '2026-02-20',
    amount: 120000,
    status: 'settled',
    statusText: 'Settled',
    colorTheme: 'emerald'
  }];
  // Helper to map color theme to explicit Tailwind classes (fixing the dynamic class bug)
  const getColorClasses = (theme: string) => {
    switch (theme) {
      case 'emerald':
        return {
          bg: 'bg-emerald-100',
          text: 'text-emerald-600',
          fill: 'bg-emerald-500',
          textFill: 'text-emerald-500'
        };
      case 'amber':
        return {
          bg: 'bg-amber-100',
          text: 'text-amber-600',
          fill: 'bg-amber-500',
          textFill: 'text-amber-500'
        };
      case 'blue':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-600',
          fill: 'bg-blue-500',
          textFill: 'text-blue-500'
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-600',
          fill: 'bg-gray-500',
          textFill: 'text-gray-500'
        };
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 15
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50" data-id="element-345">
      <TopBar title="Claims" onNotificationClick={() => navigate('notifications')} data-id="element-346" />

      <div className="px-5 pt-4 pb-6" data-id="element-347">
        {/* Header Section */}
        <div className="bg-[#1B3B36] rounded-3xl p-6 text-white mb-8 relative overflow-hidden shadow-xl shadow-emerald-900/20" data-id="element-348">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-349"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-emerald-500/20 rounded-full blur-xl -ml-10 -mb-10" data-id="element-350"></div>

          <div className="relative z-10" data-id="element-351">
            <p className="text-emerald-400 text-xs font-bold uppercase tracking-widest mb-2" data-id="element-352">
              Claims Center
            </p>
            <h1 className="text-2xl font-bold mb-4" data-id="element-353">Need to make a claim?</h1>
            <p className="text-sm text-gray-300 mb-6 max-w-[240px] leading-relaxed" data-id="element-354">
              We've made our claims process fast, transparent, and hassle-free.
            </p>

            <button onClick={() => setShowNewClaim(true)} className="bg-amber-500 hover:bg-amber-400 text-[#1B3B36] font-bold py-3 px-6 rounded-xl flex items-center gap-2 transition-all active:scale-95 shadow-lg shadow-amber-500/30" data-id="element-355">
              <PlusIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-356" /> File New Claim
            </button>
          </div>
        </div>

        {/* Claims List */}
        <div data-id="element-357">
          <div className="flex justify-between items-center mb-4 px-1" data-id="element-358">
            <h2 className="text-lg font-bold text-[#1B3B36]" data-id="element-359">Your Claims</h2>
            <button className="text-xs font-bold text-gray-500 hover:text-[#1B3B36] transition-colors" data-id="element-360">
              View All
            </button>
          </div>

          <div className="space-y-4" data-id="element-361">
            {claims.map(claim => {
            const colors = getColorClasses(claim.colorTheme);
            return <motion.div key={claim.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer group" data-id="element-362">
                  <div className="flex justify-between items-start mb-4" data-id="element-363">
                    <div className="flex items-center gap-3" data-id="element-364">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors.bg} ${colors.text}`} data-id="element-365">
                        <claim.icon className="w-5 h-5" data-id="element-366" />
                      </div>
                      <div data-id="element-367">
                        <h3 className="font-bold text-[#1B3B36] text-sm" data-id="element-368">
                          {claim.product}
                        </h3>
                        <p className="text-xs text-gray-500 font-medium" data-id="element-369">
                          {claim.id}
                        </p>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-370">
                      <span className="block font-bold text-[#1B3B36]" data-id="element-371">
                        {formatNaira(claim.amount)}
                      </span>
                      <span className="text-[10px] font-bold text-gray-400" data-id="element-372">
                        {formatDate(claim.date)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-50" data-id="element-373">
                    <div className="flex items-center gap-2" data-id="element-374">
                      <div className="relative flex h-2.5 w-2.5" data-id="element-375">
                        {claim.status === 'review' && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${colors.fill}`} data-id="element-376"></span>}
                        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${colors.fill}`} data-id="element-377"></span>
                      </div>
                      <span className={`text-xs font-bold ${colors.textFill}`} data-id="element-378">
                        {claim.statusText}
                      </span>
                    </div>
                    <button className="text-xs font-bold text-gray-400 group-hover:text-[#1B3B36] transition-colors flex items-center gap-1" data-id="element-379">
                      Track Status
                    </button>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>

      {/* New Claim Modal */}
      <AnimatePresence data-id="element-380">
        {showNewClaim && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowNewClaim(false)} className="fixed inset-0 bg-[#1B3B36]/60 backdrop-blur-sm z-50" data-id="element-381" />

            <motion.div initial={{
          opacity: 0,
          y: '100%'
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-[2rem] p-6 pb-safe max-h-[85vh] overflow-y-auto" data-id="element-382">
              <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mb-6" data-id="element-383"></div>

              <h2 className="text-2xl font-bold text-[#1B3B36] mb-2" data-id="element-384">
                File a Claim
              </h2>
              <p className="text-sm text-gray-500 mb-6" data-id="element-385">
                Select the policy you want to claim against.
              </p>

              <div className="space-y-3 mb-8" data-id="element-386">
                {/* Mock policies to select from */}
                <div className="p-4 rounded-2xl border-2 border-gray-100 hover:border-amber-500 cursor-pointer transition-colors flex items-center justify-between group" data-id="element-387">
                  <div className="flex items-center gap-3" data-id="element-388">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600" data-id="element-389">
                      <CarIcon className="w-5 h-5" data-id="element-390" />
                    </div>
                    <div data-id="element-391">
                      <h4 className="font-bold text-[#1B3B36] text-sm" data-id="element-392">
                        Motor Vehicle
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-393">LWY/MOT/2026/089</p>
                    </div>
                  </div>
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 group-hover:border-amber-500" data-id="element-394"></div>
                </div>

                <div className="p-4 rounded-2xl border-2 border-gray-100 hover:border-amber-500 cursor-pointer transition-colors flex items-center justify-between group" data-id="element-395">
                  <div className="flex items-center gap-3" data-id="element-396">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-397">
                      <SmartphoneIcon className="w-5 h-5" data-id="element-398" />
                    </div>
                    <div data-id="element-399">
                      <h4 className="font-bold text-[#1B3B36] text-sm" data-id="element-400">
                        Device Insurance
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-401">COR/DEV/2026/142</p>
                    </div>
                  </div>
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 group-hover:border-amber-500" data-id="element-402"></div>
                </div>
              </div>

              <div className="bg-amber-50 rounded-2xl p-4 flex gap-3 mb-8 border border-amber-100" data-id="element-403">
                <AlertCircleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-404" />
                <p className="text-xs text-amber-800 leading-relaxed font-medium" data-id="element-405">
                  Please have your incident report, photos, and any relevant
                  receipts ready. You'll need to upload them in the next steps.
                </p>
              </div>

              <button onClick={() => setShowNewClaim(false)} className="w-full bg-[#1B3B36] text-white font-bold py-4 rounded-xl shadow-lg shadow-emerald-900/20 active:scale-95 transition-transform" data-id="element-406">
                Continue
              </button>
            </motion.div>
          </>}
      </AnimatePresence>
    </motion.div>;
}